import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Stack, TextField, Tooltip, Typography } from "@mui/material";
import LockIcon from '@mui/icons-material/Lock';
import {
	ConcurrentEdit,
	ESRAggregateFieldProps,
	ESRQuestionConfig,
	ESRState,
} from "../types/data";
import { ESRPrimitiveField } from "./ESRPrimitiveField";
import { ESRAggregateField } from "./ESRAggregateField";
import { useRemoteField } from "../hooks/useRemoteField";
import { useParams } from "react-router-dom";
import dayjs from "dayjs";
import { Clear, EditOutlined, PersonAdd } from "@mui/icons-material";
import { ThreadToggle } from "./ThreadToggle";
import {ESRThunkDispatch, updateLastChangedESRQuestionID, updateQuestionResponse} from "../actions/form";
import InfoIcon from "@mui/icons-material/Info";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { GrantEditPermissionDialog } from "./shared/GrantEditPermissionDialog";

function resolveEffectiveLockState(
	locked: 0 | 1 | 2 | -1 | 3,
	tableEditMode: boolean,
	hasConcurrentEdits: boolean,
	isPending: boolean,
): 0 | 1 | 2 | -1 | 3 {
	// Another user has a pending edit and we haven't started editing ourselves — lock out.
	// Note: there is a potential race condition here: two users could both start editing
	// before either has saved a pending response, resulting in concurrent edits. A proper
	// fix would require a server-side lock (e.g. a QuestionLock table), but the window is
	// small enough in practice that we accept it for now.
	if (hasConcurrentEdits && !isPending) return 2;
	// The table's pencil button was clicked — all pencil-locked (1) questions in it become editable (-1) and admin granted (3)
	if (tableEditMode && (locked === 1 || locked === 3)) return -1;
	return locked;
}

export const ESRQuestion = (props: ESRQuestionConfig & { tableEditMode?: boolean }) => {
	const {
		question_id,
		field_type,
		show_date,
		show_responder,
		hint_value,
		locked,
		deletable,
		staticValue,
		autofill,
		show_initial_response,
		tableEditMode,
	} = props;

	const { esrId: esr_id = "", activeLabel = "", activeId = "" } = useParams();

	const [, , , timestamp, responderId] = useRemoteField(question_id);

	const dispatch = useDispatch<ESRThunkDispatch>();

	const [user, setUser] = useCurrentUser();
	const [permissionDialogOpen, setPermissionDialogOpen] = useState(false);
	const [permissionUserId, setPermissionUserId] = useState("");

	const esr_submitted = useSelector((state: ESRState) => state.esr_submitted);
	const responses = useSelector((state: ESRState) => state.responses);
	const committed_responses = useSelector((state: ESRState) => state.committed_responses);
	const all_responses = useSelector((state: ESRState) => state.all_responses);

	// for externally fetched fields
	const fetch_is_complete = useSelector((state: ESRState) => state.fetch_external.complete);
	const autofillStatus = (autofill.includes('PPS') || autofill.includes('WMFT')) ? true : false;

	const showThreads = esr_submitted && field_type !== "request_info"; // TODO: the new header field type

	const [previousValue, setPreviousValue] = useState<string | null>(null);
	const [lockState, setLockState] = useState(locked);
	const isPending = useSelector((state: ESRState) => !!state.responses[question_id]?.pending);
	const concurrentEdits = useSelector((state: ESRState) => state.concurrent_edits[question_id] ?? []);
	const effectiveLockState = resolveEffectiveLockState(lockState, tableEditMode ?? false, concurrentEdits.length > 0, isPending);
	const [highlight, setHighlight] = useState(false);

	useEffect(() => {
		setLockState(locked);
	}, [locked]);

	useEffect(() => {
		if (!staticValue) {
			let prevValue = null;
			let foundCurrent = false;
			const allLabels = [
				"initial",
				...Object.keys(all_responses)
					.filter((label) => label !== "initial")
					.sort(),
			];
			allLabels.forEach((label) => {
				if (label === activeLabel) {
					foundCurrent = true;
				}
				if (
					all_responses[label]?.responses.hasOwnProperty(question_id) &&
					!foundCurrent
				) {
					prevValue = all_responses[label].responses[question_id].response;
				}
			});
			// if (prevValue != previousValue) {
			// 	setPreviousValue(prevValue);
			// }
			if ((activeId !== '' && esr_id != activeId) && responses.hasOwnProperty(question_id)) {
				const currentVal = responses[question_id].response;
				if (prevValue !== null) {
					if (currentVal !== prevValue && field_type !== 'date') {
						setHighlight(true);
					} 
					else if(field_type === 'date' && currentVal !== 'x'){
						if (currentVal !== prevValue) {
							setHighlight(true);
						} 
					}
					else {
						setHighlight(false);
					}
				} else {
					setHighlight(true);
				}
			} else {
				setHighlight(false);
			}
		}
	}, [activeId, responses, all_responses]);

	return (
		<>
			{!autofillStatus || fetch_is_complete ? (
				<>
					<Stack
						direction="row"
						alignItems="center"
						id={question_id.replaceAll(".", "__")}
					>
						<Box
							sx={{
								minWidth: 0,
								flex: "auto",
								bgcolor: isPending
									? "#fffde7"
									: highlight
										? "lightyellow"
										: null,
							}}
						>
							{field_type.startsWith("list/") ? (
								<>
									{props.field_options.length < 0 &&
									props.field_options[0].length > 0 ? (
										<Box
											display="flex"
											alignItems="center"
											gap={1}
											marginBottom={2}
										>
											<InfoIcon color="error" fontSize="small" />
											<Typography variant="body2" color="error">
												{props.field_options[0]}
											</Typography>
										</Box>
									) : (
										<></>
									)}
									<ESRAggregateField
										{...(props as ESRAggregateFieldProps)}
										locked={effectiveLockState}
										singleRow={false}
									/>
								</>
							) : field_type.startsWith("listone/") ? (
								<>
									{props.field_options[0].length > 0 ? (
										<Box
											display="flex"
											alignItems="center"
											gap={1}
											marginBottom={2}
										>
											<InfoIcon color="error" fontSize="small" />
											<Typography variant="body2" color="error">
												{props.field_options[0]}
											</Typography>
										</Box>
									) : (
										<></>
									)}
									<ESRAggregateField
										{...(props as ESRAggregateFieldProps)}
										locked={effectiveLockState}
										singleRow={true}
									/>
								</>
							) : (
								<ESRPrimitiveField
									{...props}
									locked={effectiveLockState}
									hint_value={
										staticValue !== null && field_type != "file_upload"
											? previousValue !== null && show_initial_response === "x"
												? previousValue
												: hint_value
											: ""
									}
								/>
							)}
						</Box>
						<Box flex="none">
							{effectiveLockState === 1 &&
							field_type != "simple_header" &&
							field_type != "label" &&
							field_type != "header" &&
							!field_type.startsWith("list/") &&
							field_type !== "expiry_text" &&
							field_type !== "copy_text" &&
							field_type !== "summary" ? (
								<IconButton
									aria-label="unlock question"
									size="small"
									onClick={() => {
										console.log("question_id:", question_id);
										dispatch(updateLastChangedESRQuestionID(question_id));
										setLockState(-1);
									}}
								>
									<EditOutlined fontSize="small" color="primary" />
								</IconButton>
							) : null}
							{effectiveLockState === 3 &&
							field_type != "simple_header" &&
							field_type != "label" &&
							field_type != "header" &&
							!field_type.startsWith("list/") &&
							field_type !== "expiry_text" &&
							field_type !== "copy_text" &&
							field_type !== "summary" ? (
								<IconButton
									aria-label="unlock question"
									size="small"
									onClick={() => {
										// console.log("question_id:", question_id);
										dispatch(updateLastChangedESRQuestionID(question_id));
										setLockState(-1);
									}}
								>
									{/* <EditOutlined fontSize="small" color="primary" /> */}
									<LockIcon fontSize="small" color="primary"/>
								</IconButton>
							) : null}
							{user?.admin && (
								<Tooltip title="Grant Question Permission">
									<IconButton
										aria-label="grant permission"
										size="small"
										onClick={() => setPermissionDialogOpen(true)}
									>
										<PersonAdd fontSize="small" color="secondary" />
									</IconButton>
								</Tooltip>
							)}
							{(effectiveLockState === 0 || effectiveLockState === -1) &&
							deletable === 0 &&
							field_type !== "header" &&
							field_type !== "label" &&
							field_type !== "expiry_text" &&
							field_type !== "copy_text" &&
							field_type !== "summary" ? (
								<Tooltip title={`Clear ${question_id}`}>
									<IconButton
										aria-label={`Clear response for ${question_id}`}
										size="small"
										onClick={() => {
											dispatch(
												updateQuestionResponse(
													esr_id,
													activeId,
													question_id,
													"",
													field_type,
													"single",
													effectiveLockState === -1,
												),
											);
										}}
									>
										<Clear fontSize="small" color="inherit" />
									</IconButton>
								</Tooltip>
							) : null}
							{showThreads ? <ThreadToggle questionId={question_id} /> : null}
						</Box>
					</Stack>
					<Typography color="gray" fontSize={12}>
						{(show_date || field_type === "file_upload") && timestamp
							? `${dayjs(timestamp).format("YYYY-MM-DD hh:mm A")}`
							: "\xa0"}
					</Typography>
					<Typography color="gray" fontSize={12}>
						{show_responder && responderId
							? `Updated by: ${responderId}`
							: "\xa0"}
					</Typography>
					{isPending ? (
						<Typography color="gray" fontSize={12} marginLeft={"2px"}>
							Committed answer:{" "}
							{question_id in committed_responses &&
							committed_responses[question_id]?.response?.length > 0
								? committed_responses[question_id]?.response
										.replace("[", "")
										.replace("]", "")
										.replace("\\", "")
										.replace("True", "Yes")
										.replace("False", "No")
								: "None"}
						</Typography>
					) : null}
					{concurrentEdits.length > 0 && (
						<Box sx={{ mt: 0.5 }}>
							<Typography color="gray" fontSize={12} marginLeft={"2px"}>
								Pending edits by others:
							</Typography>
							{concurrentEdits.map((edit: ConcurrentEdit) => (
								<Box
									key={edit.responder_id}
									sx={{
										mt: 0.5,
										ml: 1,
										pl: 1,
										borderLeft: "3px solid #ccc",
									}}
								>
									<Typography fontSize={12} fontWeight="bold" color="gray">
										{edit.responder_id}
									</Typography>
									<Typography fontSize={12} color="gray">
										{edit.response || "(empty)"}
									</Typography>
								</Box>
							))}
						</Box>
					)}
				</>
			) : (
				<Box sx={{ display: "flex", position: "relative", left: "20%" }}>
					<CircularProgress />
				</Box>
			)}
			{/* <Dialog
				open={permissionDialogOpen}
				onClose={() => setPermissionDialogOpen(false)}
			>
				<DialogTitle>Grant Permission for {question_id}</DialogTitle>

				<DialogContent>
					<TextField
						fullWidth
						label="User ID"
						value={permissionUserId}
						onChange={(e) => setPermissionUserId(e.target.value)}
						margin="dense"
					/>
				</DialogContent>

				<DialogActions>
					<Button onClick={() => setPermissionDialogOpen(false)}>Cancel</Button>

					<Button
						variant="contained"
						onClick={() => {
							dispatch(
								updateQuestionResponse(
									esr_id,
									activeId,
									`${question_id}_SPECIAL_UNLOCK`,
									permissionUserId,
									field_type,
									"single",
									effectiveLockState === -1,
								),
							);
							setPermissionUserId("");
							setPermissionDialogOpen(false);
						}}
					>
						Grant
					</Button>
				</DialogActions>
			</Dialog> */}
			<GrantEditPermissionDialog 
				open={permissionDialogOpen}
				title={`Grant Permission for ${question_id}`}
				onClose={() => setPermissionDialogOpen(false)}
				onGrant={(email) => {
					// Always saved as committed (never pending) so the grantee can see it
					dispatch(
						updateQuestionResponse(
							esr_id,
							activeId,
							`${question_id}_SPECIAL_UNLOCK`,
							email,
							field_type,
							"single",
							false,
						),
					);
				}}
			/>
		</>
	);
};
